/** Answer normalisation and comparison for auto-checked exercises (content-schema.md §3). */

const CJK_PUNCT: Record<string, string> = {
  '。': '.',
  '、': ',',
  '“': '"',
  '”': '"',
  '‘': "'",
  '’': "'",
  '「': '"',
  '」': '"',
  '『': '"',
  '』': '"',
  '《': '"',
  '》': '"',
  '…': '...',
  '—': '-',
  '–': '-',
};

const PUNCT_RE = /[\p{P}\p{S}]/gu;
const HAN_RE = /\p{Script=Han}/u;

export function containsHan(text: string): boolean {
  return HAN_RE.test(text);
}

/** NFKC folds full-width ASCII forms (，！？Ａ１) to their half-width equivalents. */
export function normalizeAnswer(text: string): string {
  return Array.from(text.normalize('NFKC'))
    .map((ch) => CJK_PUNCT[ch] ?? ch)
    .join('')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalised form without punctuation (apostrophes kept, so "I'm" ≠ "Im");
 * whitespace is dropped entirely for Chinese.
 */
export function answerKey(text: string): string {
  const spaced = normalizeAnswer(text)
    .replace(PUNCT_RE, (ch) => (ch === "'" ? ch : ' '))
    .replace(/\s+/g, ' ')
    .trim();
  return containsHan(spaced) ? spaced.replace(/\s/g, '') : spaced;
}

export function sameAnswer(expected: string, actual: string): boolean {
  return answerKey(expected) === answerKey(actual) && answerKey(actual) !== '';
}

const TONE_MARKS_RE = /[\u0300\u0301\u0304\u030C]/g;

/**
 * Pinyin without tones, spaces, apostrophes or punctuation, so that
 * "ēn'ài", "en1 ai4" and "EN AI" compare equal. ü may be typed as ü, v or u:.
 */
export function tonelessPinyin(text: string): string {
  return text
    .normalize('NFD')
    .replace(TONE_MARKS_RE, '')
    .normalize('NFC')
    .toLowerCase()
    .replace(/u:/g, 'ü')
    .replace(/v/g, 'ü')
    .replace(/[1-5]/g, '')
    .replace(/[\s'’\-·]/g, '')
    .replace(PUNCT_RE, '');
}

export function samePinyin(expected: string, actual: string): boolean {
  const a = tonelessPinyin(actual);
  return a !== '' && tonelessPinyin(expected) === a;
}

export type DiffOp = 'same' | 'missing' | 'extra';
export interface DiffSegment {
  op: DiffOp;
  text: string;
}

/** Character-level diff (LCS). `missing` = expected but not typed, `extra` = typed but wrong. */
export function diffChars(expected: string, actual: string): DiffSegment[] {
  const a = Array.from(expected);
  const b = Array.from(actual);
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      lcs[i]![j] =
        a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const segments: DiffSegment[] = [];
  const push = (op: DiffOp, ch: string) => {
    const last = segments[segments.length - 1];
    if (last && last.op === op) last.text += ch;
    else segments.push({ op, text: ch });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i]!);
      i += 1;
      j += 1;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      push('missing', a[i]!);
      i += 1;
    } else {
      push('extra', b[j]!);
      j += 1;
    }
  }
  while (i < a.length) push('missing', a[i++]!);
  while (j < b.length) push('extra', b[j++]!);
  return segments;
}

export interface DictationResult {
  correct: boolean;
  /** What the learner typed was compared as characters or as toneless pinyin. */
  mode: 'hanzi' | 'pinyin' | 'text';
  diff: DiffSegment[];
}

/**
 * Chinese dictation accepts either characters (typed through a pinyin IME, as in the
 * computer-based HSK) or toneless pinyin. Other languages compare text without punctuation.
 */
export function checkDictation(
  expected: { text: string; pinyin?: string | undefined },
  input: string,
): DictationResult {
  if (containsHan(expected.text)) {
    if (containsHan(input) || !expected.pinyin) {
      const want = answerKey(expected.text);
      const got = answerKey(input);
      return { correct: want === got, mode: 'hanzi', diff: diffChars(want, got) };
    }
    const want = tonelessPinyin(expected.pinyin);
    const got = tonelessPinyin(input);
    return { correct: want === got, mode: 'pinyin', diff: diffChars(want, got) };
  }
  const want = answerKey(expected.text);
  const got = answerKey(input);
  return { correct: want === got, mode: 'text', diff: diffChars(want, got) };
}
